/**
 * PipelineEditor — top-level structured-pipeline UI.
 *
 * Owns editor state via usePipelineEditorState. Renders:
 *   - Header: preset chip + reset
 *   - Body: stage cards with module rows + inline detail expansion
 *   - Right rail: live cost panel
 *
 * Emits the current PipelineDoc upward via onDocChange. The host
 * (StructuredInput) is responsible for serializing to YAML and uploading
 * via the existing useUploadFile flow — this component does not touch
 * the wire format.
 */

import { useEffect, useMemo } from 'react';

import { estimatePipeline } from './costEstimator';
import {
  CONSTANTINE_MAX_BUDGET_USD,
  CostPanel,
  type ModuleLlmOverrideAnnotation,
} from './CostPanel';
import {
  type PipelineSummary,
  useReportPipelineSummary,
} from './PipelineSummaryContext';
import { PresetChip } from './PresetChip';
import { StageRow } from './StageRow';
import type { PipelineDoc, PipelineManifest, TargetMetrics } from './types';
import { toModuleRefObject } from './types';
import { usePipelineEditorState } from './usePipelineEditorState';

/**
 * Walk every ModuleRefObject in the doc and collect any `llm:` block whose
 * provider differs from the top-level (or where the top-level provider is
 * unset and the module override declares a routing provider). These are the
 * cross-provider overrides the cost panel should caveat with a chip.
 */
function collectModuleLlmOverrides(
  doc: PipelineDoc
): ModuleLlmOverrideAnnotation[] {
  const topProvider = doc.llm?.provider;
  const out: ModuleLlmOverrideAnnotation[] = [];
  for (const stage of doc.pipeline) {
    for (const mod of stage.modules) {
      const refObj = toModuleRefObject(mod);
      const moduleLlm = refObj.llm;
      if (!moduleLlm) continue;
      const moduleProvider = moduleLlm.provider;
      if (!moduleProvider) continue;
      if (moduleProvider === topProvider) continue;
      const model =
        moduleLlm.models?.fast ??
        moduleLlm.models?.standard ??
        moduleLlm.models?.deep ??
        '';
      out.push({
        module: refObj.name,
        provider: moduleProvider,
        model,
      });
    }
  }
  return out;
}

interface PipelineEditorProps {
  manifest: PipelineManifest;
  initialPresetName?: string;
  target?: TargetMetrics;
  /**
   * When true, the backend's SSM-stored GitHub credential was rejected
   * AND the unauth fallback couldn't reach the repo — surface a loud
   * "auth failed, cost cannot be calculated" panel instead of the
   * normal cost breakdown (which would show placeholder defaults).
   */
  metricsAuthRequired?: boolean;
  /**
   * When true, the metrics fetch returned `pending` — typically because
   * the git-tree fetch failed (rate-limit, truncation, network). Cost
   * panel shows a "metrics pending" state and the projection is
   * suppressed; never silently fall back to a placeholder fudge.
   */
  metricsPending?: boolean;
  onDocChange: (doc: PipelineDoc) => void;
}

export function PipelineEditor({
  manifest,
  initialPresetName,
  target,
  metricsAuthRequired,
  metricsPending,
  onDocChange,
}: PipelineEditorProps) {
  const state = usePipelineEditorState({ manifest, initialPresetName });

  useEffect(() => {
    onDocChange(state.doc);
  }, [state.doc, onDocChange]);

  const estimate = useMemo(
    () => estimatePipeline(state.doc, manifest, target),
    [state.doc, manifest, target]
  );

  const moduleLlmOverrides = useMemo(
    () => collectModuleLlmOverrides(state.doc),
    [state.doc]
  );

  // Broadcast current preset + cost up to the drawer so the Run
  // Summary on step 3 can show them, and the Run button can block on
  // over-budget. Memoized so we only re-broadcast when these specific
  // values change (avoids loops in the consumer's useEffect dep).
  const broadcast: PipelineSummary = useMemo(() => {
    const activePreset = manifest.presets.find(
      p => p.name === state.basePresetName
    );
    const presetDisplayName =
      state.mode === 'modified'
        ? `${activePreset?.display_name ?? 'Custom'} (modified)`
        : state.mode === 'upload'
          ? 'Imported YAML'
          : (activePreset?.display_name ?? 'Custom');
    const budget = CONSTANTINE_MAX_BUDGET_USD;
    return {
      presetDisplayName,
      presetSummary:
        state.mode === 'preset' ? activePreset?.summary : undefined,
      costIndicator: activePreset?.cost_indicator,
      projectedCost: estimate.total.p50,
      projectedHigh: estimate.total.high,
      budget,
      overBudget: estimate.total.p50 > budget,
      hasUnavailable: estimate.hasUnavailable,
    };
  }, [manifest.presets, state.basePresetName, state.mode, estimate]);

  useReportPipelineSummary(broadcast);

  const stageCount = state.doc.pipeline.length;

  return (
    <div className="flex flex-col gap-4">
      <PresetChip
        presets={manifest.presets}
        selectedPresetName={state.basePresetName}
        mode={state.mode}
        onSelectPreset={state.selectPreset}
        onResetToPreset={state.resetToPreset}
      />

      <div className="flex gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-3">
          {state.doc.pipeline.map((stage, stageIndex) => (
            <StageRow
              key={`${stage.name}-${stageIndex}`}
              stage={stage}
              stageIndex={stageIndex}
              isLast={stageIndex === stageCount - 1}
              manifest={manifest}
              onReplaceModule={(moduleIndex, next) =>
                state.replaceModule(stageIndex, moduleIndex, next)
              }
              onAppendModule={mod => state.appendModule(stageIndex, mod)}
              onRemoveModule={moduleIndex =>
                state.removeModule(stageIndex, moduleIndex)
              }
              moduleConfigOverrides={state.doc.module_config ?? {}}
              onSetModuleConfig={state.setModuleConfig}
            />
          ))}
          {stageCount === 0 && (
            <p className="text-chariot-text-secondary italic">
              Select a preset to begin.
            </p>
          )}
        </div>

        <CostPanel
          estimate={estimate}
          enforceBudget={state.doc.llm?.enforce_budget}
          metricsAuthRequired={metricsAuthRequired}
          metricsPending={metricsPending}
          moduleLlmOverrides={moduleLlmOverrides}
        />
      </div>
    </div>
  );
}
